import React, { useState, useEffect, useCallback, useRef } from "react";
import { useForm } from "react-hook-form";
import { Auth } from "aws-amplify";
import { useNavigate, useLocation } from "react-router-dom";
import { useLoading } from "../../../../context/LoadingContext";
import { jwtDecode } from "jwt-decode";
import { getParameterFromManager } from "../../../../services/Utils";
import "../../../../styles/general.css";
import "../../../../styles/amtpl.css";
import SidebarCommon from "../../../../pages/Requirement/components/layout/AMTPLSidebar";
import Header from "../../../../pages/Requirement/components/layout/Header";
import DataTable from "../../../../pages/Requirement/components/common/ReusableTable";
import SearchFilter from "../../../../pages/Requirement/components/common/SearchFilter";
import AsyncImage from "../../../../pages/Requirement/components/common/AsyncImage";
import {
  TextField,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  FormHelperText,
} from "@mui/material";
import {
  fetchData,
  searchfetchQueries,
  postQuery,
  uploadFileToS3,
} from "../../../../pages/Requirement/services/RequirementServices";
import { v4 as uuidv4 } from "uuid";
import AlertModal from "../../../../pages/Requirement/components/common/AlerModel";

const Product = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const initialFilter = location.state?.selectedFilter || "productcategory";
  const [activeUser, setActiveUser] = useState("");
  const [selectedFilter, setSelectedFilter] = useState(initialFilter);
  const { setLoading } = useLoading();
  const [rowStart, setRowStart] = useState(0);
  const [pageSize] = useState(5);
  const [totalItems, setTotalItems] = useState(0);
  const [products, setProducts] = useState([]);
  const [INRToUsd, setINRToUSD] = useState(0.012);
  const [showForm, setShowForm] = useState(false);
  const [searchValues, setSearchValues] = useState({
    searchCategory: "",
    searchProduct: "",
    searchProductGrade: "",
    searchStatus: "",
  });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editProductID, setEditProductID] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [productCategoryList, setProductCategoryList] = useState([]);
  const [modal, setModal] = useState({
    show: false,
    type: "info",
    title: "",
    message: "",
    nextAction: null,
    autoClose: null,
  });
  const [filePreview, setFilePreview] = useState(null);
  const [fileError, setFileError] = useState("");

  const formRef = useRef(null);
  const fileInputRef = useRef(null);
  const { control, setValue, reset } = useForm();
  const [productList, setProductList] = useState([]);
  const [productGradeList, setProductGradeList] = useState([]);

  const inputStyles = {
    height: "50px",
    borderRadius: "4px",
    border: "1px solid #ccc",
    paddingLeft: "10px",
  };

  const handleModalClose = () => {
    const action = modal.nextAction;
    setModal((prev) => ({ ...prev, show: false, nextAction: null }));
    if (action) action();
  };

  // Form state
  const [formData, setFormData] = useState({
    productCategoryID: "",
    productName: "",
    grade: "",
    status: "",
    file: null,
  });

  // State for errors
  const [errors, setErrors] = useState({});

  const loadProductCategoryList = useCallback(async () => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProductCategory",
          userOrder: "CategoryName",
          rowStart: 0,
          pageSize: 1,
        }),
      ]);

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const [allItems] = await Promise.all([
          searchfetchQueries({
            target: "listProductCategory",
            userOrder: "CategoryName",
            rowStart: 0,
            pageSize: totalCount,
          }),
        ]);

        setProductCategoryList(allItems);
      } else {
        setProductCategoryList([]);
      }
    } catch (error) {
      console.error("Error loading listProductCategory: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadProductList = useCallback(async (categoryId) => {
    setLoading(true);
    try {
      const [initialResponse] = await Promise.all([
        fetchData({
          target: "listProducts",
          userOrder: "ProductName",
          rowStart: 0,
          pageSize: 1,
          filterTerms: { ProductCategoryID: categoryId },
        }),
      ]);

      const totalCount = initialResponse?.total || initialResponse?.length || 0;

      if (totalCount > 0) {
        const [allProducts] = await Promise.all([
          searchfetchQueries({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: totalCount,
            filterTerms: { ProductCategoryID: categoryId },
          }),
        ]);

        // Filter distinct ports based on DestinationPortAdd commentMore actions
        const distinctProducts = Array.from(
          new Map(allProducts.map((p) => [p.ProductName, p])).values()
        );
        console.log("@@distinctProducts", distinctProducts);

        setProductList(distinctProducts);
      } else {
        setProductList([]);
      }
    } catch (error) {
      console.error("Error loading products: ", error);
    } finally {
      setLoading(false);
    }
  }, [setLoading]);

  const loadProductGrades = useCallback(
    async (productName) => {
      setLoading(true);
      try {
        const [allGrades] = await Promise.all([
          fetchData({
            target: "listProducts",
            userOrder: "ProductName",
            rowStart: 0,
            pageSize: 500,
            filterTerms: { ProductName: productName },
          }),
        ]);
        if (allGrades.items.length > 0) {
          setProductGradeList(allGrades.items);
        } else {
          setProductGradeList([]);
        }
      } catch (error) {
        console.error("Error loading product grades: ", error);
      } finally {
        setLoading(false);
      }
    },
    [setLoading]
  );

  // Handle input change for the form
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Handle file change
  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    setFileError("");

    if (selectedFile) {
      // Validate file size (10MB limit)
      if (selectedFile.size > 10 * 1024 * 1024) {
        setFileError("File size must be less than 10MB");
        return;
      }

      // Validate file type
      if (!selectedFile.type.startsWith("image/")) {
        setFileError("Please upload an image file");
        return;
      }

      setFormData((prev) => ({
        ...prev,
        file: selectedFile,
      }));

      // Create preview for new file
      // const reader = new FileReader();
      // reader.onloadend = () => {
      //   setFilePreview(reader.result);
      // };
      // reader.readAsDataURL(selectedFile);
    } else {
      setFormData((prev) => ({
        ...prev,
        file: null,
      }));
      setFilePreview(isEditMode ? selectedRow?.ProductImagePath : null);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.productCategoryID)
      newErrors.productCategoryID = "Product category is required";
    if (!formData.productName)
      newErrors.productName = "Product name is required";
    if (fileError) newErrors.file = fileError;

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Save form data with file upload
  const saveFormData = async (data) => {
    setLoading(true);
    try {
      let body = null;
      const productInput = {
        ProductID: data.id,
        ProductCategoryID: data.productCategoryID,
        ProductName: data.productName,
        Grade: data.grade || null,
        Status: data.status || "ACTIVE",
      };

      let fileName = isEditMode ? selectedRow?.ProductImagePath : "";
      if (data.file) {
        const fileresult = await uploadFileToS3(data.file);
        if (fileresult && fileresult.fileName) {
          fileName = fileresult.fileName;
        }
      }
      productInput.ProductImagePath = fileName;

      if (isEditMode) {
        body = {
          target: "updateProduct",
          updateProductInput: productInput,
        };
      } else {
        body = {
          target: "createProduct",
          createProductInput: productInput,
        };
      }

      const result = await postQuery(body);
      if (result) {
        setModal({
          show: true,
          type: "success",
          title: "Success",
          message: `Product ${isEditMode ? "updated" : "added"} successfully!`,
          autoClose: 2000,
        });
      }

      // Refresh the table data
      await searchProduct(searchValues, rowStart);
    } catch (error) {
      console.error("Error saving data:", error);
      setModal({
        show: true,
        type: "error",
        title: "Error",
        message: `Failed to ${isEditMode ? "update" : "add"} product.`,
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (validateForm()) {
      const dataToSave = {
        ...formData,
        id: isEditMode ? editProductID : uuidv4(),
      };
      await saveFormData(dataToSave);
      setShowForm(false);
      setIsEditMode(false);
      setEditProductID(null);
      setSelectedRow(null);
      setFilePreview(null);
      setFormData({
        productCategoryID: "",
        productName: "",
        grade: "",
        status: "",
        file: null,
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Handle form reset
  const handleReset = () => {
    setFormData({
      productCategoryID: "",
      productName: "",
      grade: "",
      status: "",
      file: null,
    });
    setErrors({});
    setFileError("");
    setFilePreview(null);
    setIsEditMode(false);
    setEditProductID(null);
    setSelectedRow(null);
    setShowForm(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    reset();
  };

  const handleDataFilterChange = useCallback(
    (event) => {
      const selectedValue = event.target.value;
      setSelectedFilter(selectedValue);
      console.log("Selected filter:", selectedValue);
      if (
        selectedValue === "products" ||
        selectedValue === "offers" ||
        selectedValue === "entities"
      ) {
        navigate("/amtpl", { state: { selectedFilter: selectedValue } });
      } else if (selectedValue === "Add Requirement") {
        navigate("/amtpl/addrequirement", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Requirement Approval") {
        navigate("/amtpl/requirement", {
          state: { selectedValue },
        });
      } else if (selectedValue === "Product Offers") {
        navigate("/amtpl/productofferscharges", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Inland Shipping") {
        navigate("/amtpl/inlandshipping", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Country wise Product") {
        navigate("/amtpl/counrywiseproduct", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Product Category") {
        navigate("/amtpl/productcategory", {
          state: { selectedFilter: selectedValue },
        });
      } else if (selectedValue === "Product") {
        navigate("/amtpl/product", {
          state: { selectedFilter: selectedValue },
        });
      }
    },
    [navigate]
  );

  const searchProduct = useCallback(
    async (searchValue, newRowStart = rowStart) => {
      setLoading(true);
      const body = {
        target: "listProducts",
        userOrder: "ProductName",
        rowStart: newRowStart,
        pageSize: pageSize,
        filterTerms: {
          ProductCategoryID: searchValue.searchCategory || "",
          ProductNamePartial: searchValue.searchProduct || "",
          Grade: searchValue.searchProductGrade || "",
          Status: searchValue.searchStatus || "",
        },
      };

      try {
        const response = await fetchData(body);
        setProducts(response.items || response);
        setTotalItems(response.total || 0);
      } catch (error) {
        console.error("Error loading listProducts: ", error);
        setProducts([]);
        setTotalItems(0);
      } finally {
        setLoading(false);
      }
    },
    [rowStart, pageSize]
  );

  const handleSearch = (searchTerm) => {
    setSearchValues(searchTerm);
    setRowStart(0);
    searchProduct(searchTerm, 0);
  };

  const handleRowClick = useCallback(async (row) => {
    setSelectedRow(row);
    setIsEditMode(true);
    setEditProductID(row.ProductID);
    setShowForm(true);
    setFormData({
      productCategoryID: row.ProductCategoryID || "",
      productName: row.ProductName || "",
      grade: row.Grade || "",
      status: row.Status || "",
      file: null,
    });
    // Set filePreview to the existing ProductImagePath (S3 URL or key)
    setFilePreview(row.ProductImagePath || null);
    setFileError("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  }, []);

  // Scroll to form when showForm is true
  useEffect(() => {
    if (showForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [showForm]);

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);

        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_ADMINS")) {
          await Auth.signOut();
          navigate("/");
        } else {
          setActiveUser(user.attributes.email);
        }
        let inrToUsdVal = await getParameterFromManager("INR_TO_USD");
        inrToUsdVal = parseFloat(inrToUsdVal);
        setINRToUSD(inrToUsdVal);
      } catch (error) {
        await Auth.signOut();
        navigate("/");
      }
    };

    checkUser();
    loadProductCategoryList();
    
  }, [navigate, loadProductCategoryList]);

  useEffect(() => {
    searchProduct(searchValues);
  }, [searchValues, rowStart, searchProduct]);

  const handlePageChange = (page) => {
    const newRowStart = (page - 1) * pageSize;
    setRowStart(newRowStart);
    searchProduct(searchValues, newRowStart);
  };

  const currentPage = Math.floor(rowStart / pageSize) + 1;

  const fields = [
    {
      name: "searchCategory",
      label: "Category",
      type: "dropdown",
      options: productCategoryList.map((item) => ({
        value: item.ProductCategoryID,
        label: item.CategoryName,
      })),
      value: searchValues.searchCategory,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchCategory: value }));
        loadProductList(value);
      },
      
    },
    {
      name: "searchProduct",
      label: "Product",
      type: "dropdown",
      options: productList.map((item) => ({
        value: item.ProductName,
        label: item.ProductName,
      })),
      value: searchValues.searchProduct,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProduct: value }));
        loadProductGrades(value);
      },
      
    },
    {
      name: "searchProductGrade",
      label: "Product Grade",
      type: "dropdown",
      options: productGradeList.map((item) => ({
        value: item.Grade,
        label: item.Grade,
      })),
      value: searchValues.searchProductGrade,
      onChange: (value) => {
        setSearchValues((prev) => ({ ...prev, searchProductGrade: value }));
      },
      
    },
    {
      name: "searchStatus",
      label: "Status",
      type: "dropdown",
      options: [
        { value: "ACTIVE", label: "Active" },
        { value: "INACTIVE", label: "Inactive" },
      ],
      value: searchValues.searchStatus,
      onChange: (value) =>
        setSearchValues((prev) => ({ ...prev, searchStatus: value })),
    },
  ];

  const columns = [
    {
      id: "ProductName",
      numeric: false,
      label: "Product Name",
    },
    {
      id: "Grade",
      numeric: false,
      label: "Grade",
    },
    {
      id: "ProductCategoryID",
      numeric: false,
      label: "Category",
      renderCell: (row) => {
        const item = productCategoryList.find(
          (p) => p.ProductCategoryID === row.ProductCategoryID
        );
        return item ? item.CategoryName : "";
      },
    },
    {
      id: "ProductImagePath",
      numeric: false,
      label: "Product Image",
      renderCell: (row) =>
        row.ProductImagePath ? (
          <div style={{ padding: "5px" }}>
            <AsyncImage
              imageKey={row.ProductImagePath}
              alt={row.ProductName || "Product Image"}
              width="50px"
              height="40px"
            />
          </div>
        ) : (
          <span></span>
        ),
    },
    {
      id: "Status",
      numeric: false,
      label: "Status",
    },
  ];

  return (
    <div className="amtpl-dashboard">
      <AlertModal
        type={modal.type}
        title={modal.title}
        message={modal.message}
        showModal={modal.show}
        onClose={handleModalClose}
        buttonText="OK"
        autoClose={modal.autoClose}
      />
      <Header activeUser={activeUser} setLoading={setLoading} />

      <div className="content">
        <SidebarCommon
          selectedFilter={selectedFilter}
          handleDataFilterChange={handleDataFilterChange}
        />
        <main className="main-content">
          <div className="table-header">
            <div className="header-text">
              <div className="head-text">Product List</div>
            </div>
            <div
              className="head-btn"
              style={{ display: "flex", gap: "10px", alignItems: "center" }}
            >
              <div
                className="submit-btn"
                onClick={() => {
                  setShowForm(!showForm);
                  setIsEditMode(false);
                  setFormData({
                    productCategoryID: "",
                    productName: "",
                    grade: "",
                    status: "",
                    file: null,
                  });
                  setFilePreview(null);
                  setFileError("");
                  if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                  }
                }}
              >
                Add
              </div>
            </div>
          </div>
          <div className="search-filter">
            <div className="row">
              <SearchFilter fields={fields} onSearch={handleSearch} />
            </div>
          </div>
          <div className="table-css">
            <DataTable
              columns={columns}
              data={products}
              total={totalItems}
              title="Product Information"
              rowsPerPage={pageSize}
              onRowClick={handleRowClick}
              onPageChange={handlePageChange}
              currentPage={currentPage}
            />
          </div>
          <div className="table-header">
            <div className="header-text">
              <div className="head-text"></div>
            </div>
            <div className="head-btn">
              {showForm && (
                <>
                  <div onClick={handleSubmit} className="submit-btn">
                    {isEditMode ? "Update" : "Submit"}
                  </div>
                  <div onClick={handleReset} className="submit-btn">
                    {isEditMode ? "Cancel" : "Reset"}
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Form Section */}
          {showForm && (
            <form
              ref={formRef}
              onSubmit={handleSubmit}
              style={{
                padding: "10px",
                backgroundColor: "#f9f9f9",
                borderRadius: "8px",
              }}
            >
              <div className="row">
                <div className="col-md-3">
                  <FormControl
                    sx={{ minWidth: "100%" }}
                    error={!!errors.productCategoryID}
                  >
                    <Typography variant="body2">
                      Select Product Category
                    </Typography>
                    <Select
                      name="productCategoryID"
                      value={formData.productCategoryID}
                      onChange={handleChange}
                      displayEmpty
                      sx={{ backgroundColor: "white" }}
                      inputProps={{ "aria-label": "Select Product Category" }}
                    >
                      <MenuItem value="" disabled>
                        Select Product Category
                      </MenuItem>
                      {productCategoryList.map((pc) => (
                        <MenuItem
                          key={pc.ProductCategoryID}
                          value={pc.ProductCategoryID}
                        >
                          {pc.CategoryName}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors.productCategoryID && (
                      <Typography variant="caption" color="error">
                        {errors.productCategoryID}
                      </Typography>
                    )}
                  </FormControl>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography circa="body2">Product Name</Typography>
                    <TextField
                      type="text"
                      name="productName"
                      value={formData.productName}
                      onChange={handleChange}
                      sx={{ backgroundColor: "white", width: "100%" }}
                      error={!!errors.productName}
                      helperText={errors.productName}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <div className="mb-3">
                    <Typography variant="body2">Grade</Typography>
                    <TextField
                      type="text"
                      name="grade"
                      value={formData.grade}
                      onChange={handleChange}
                      sx={{ backgroundColor: "white", width: "100%" }}
                      error={!!errors.grade}
                      helperText={errors.grade}
                    />
                  </div>
                </div>
                <div className="col-md-3">
                  <FormControl
                    sx={{ minWidth: "100%" }}
                    error={!!errors.status}
                  >
                    <Typography variant="body2">Status</Typography>
                    <Select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      displayEmpty
                      sx={{ backgroundColor: "white" }}
                      inputProps={{ "aria-label": "Select Product Category" }}
                    >
                      <MenuItem value="" disabled>
                        Select Status
                      </MenuItem>
                      <MenuItem value="ACTIVE">ACTIVE</MenuItem>
                      <MenuItem value="INACTIVE">INACTIVE</MenuItem>
                    </Select>
                    {errors.status && (
                      <Typography variant="caption" color="error">
                        {errors.status}
                      </Typography>
                    )}
                  </FormControl>
                </div>
                <div className="col-md-12">
                  <Typography variant="body2">
                    File Upload (Max 10MB) Optional
                  </Typography>
                </div>
                <div
                  className="col-md-10"
                  style={{ display: "flex", alignItems: "center" }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    style={inputStyles}
                  />
                </div>
                <div
                  className="col-md-2"
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {filePreview && (
                    <AsyncImage
                      imageKey={filePreview}
                      alt="Preview"
                      width="60px"
                      height="60px"
                      style={{
                        objectFit: "cover",
                        borderRadius: "4px",
                        border: "1px solid #ddd",
                        backgroundColor: "#f9f9f9",
                      }}
                    />
                  )}
                </div>
                <div className="col-md-row">
                  {(errors.file || fileError) && (
                    <FormHelperText
                      error
                      style={{ margin: 0, marginLeft: "10px" }}
                    >
                      {errors.file || fileError}
                    </FormHelperText>
                  )}
                </div>
              </div>
            </form>
          )}
        </main>
      </div>
    </div>
  );
};

export default Product;
